(function () {
  'use strict';

  if (window.MrMapleProductJsonLoader) return;

  const MAX_CONCURRENT_REQUESTS = 4;
  const requestCache = new Map();
  const fullRequestCache = new Map();
  const missingHandleCache = new Set();
  const MISSING_HANDLE_STORAGE_KEY = 'mrmaple:product-json-loader:missing:v1';
  const MISSING_HANDLE_TTL_MS = 6 * 60 * 60 * 1000;
  const queue = [];
  let activeRequests = 0;
  let missingHandleStorageLoaded = false;

  function normalizeHandle(handle) {
    return String(handle || '').trim().toLowerCase();
  }

  function isTransientStatus(status) {
    return status === 429 || status >= 500;
  }

  function isMissingStatus(status) {
    return status === 404 || status === 410;
  }

  function readMissingHandleStorage() {
    if (missingHandleStorageLoaded) return;
    missingHandleStorageLoaded = true;

    try {
      const raw = sessionStorage.getItem(MISSING_HANDLE_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const now = Date.now();
      let didPrune = false;

      Object.keys(parsed || {}).forEach((handle) => {
        const expiresAt = Number(parsed[handle]);
        if (!handle || !isFinite(expiresAt) || expiresAt <= now) {
          didPrune = true;
          return;
        }
        missingHandleCache.add(handle);
      });

      if (didPrune) {
        writeMissingHandleStorage();
      }
    } catch (_) {
      try {
        sessionStorage.removeItem(MISSING_HANDLE_STORAGE_KEY);
      } catch (_) {}
    }
  }

  function writeMissingHandleStorage() {
    try {
      const expiresAt = Date.now() + MISSING_HANDLE_TTL_MS;
      const payload = {};
      missingHandleCache.forEach((handle) => {
        payload[handle] = expiresAt;
      });

      if (Object.keys(payload).length) {
        sessionStorage.setItem(MISSING_HANDLE_STORAGE_KEY, JSON.stringify(payload));
      } else {
        sessionStorage.removeItem(MISSING_HANDLE_STORAGE_KEY);
      }
    } catch (_) {}
  }

  function isMissingHandle(handle) {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return false;
    readMissingHandleStorage();
    return missingHandleCache.has(normalizedHandle);
  }

  function pumpQueue() {
    while (activeRequests < MAX_CONCURRENT_REQUESTS && queue.length > 0) {
      const next = queue.shift();
      if (!next) break;

      activeRequests += 1;

      Promise.resolve()
        .then(next.task)
        .then(next.resolve, next.reject)
        .finally(() => {
          activeRequests = Math.max(0, activeRequests - 1);
          pumpQueue();
        });
    }
  }

  function enqueue(task) {
    return new Promise((resolve, reject) => {
      queue.push({ task, resolve, reject });
      pumpQueue();
    });
  }

  function markMissingHandle(handle) {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return;

    readMissingHandleStorage();
    missingHandleCache.add(normalizedHandle);
    requestCache.set(normalizedHandle, Promise.resolve(null));
    fullRequestCache.set(normalizedHandle, Promise.resolve(null));
    writeMissingHandleStorage();
  }

  async function fetchProductJson(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (isMissingStatus(response.status)) {
      markMissingHandle(handle);
      return null;
    }

    if (!response.ok) {
      const error = new Error(`Failed to load product JSON for ${handle}`);
      error.status = response.status;
      throw error;
    }

    return response.json();
  }

  async function fetchProductFullJson(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.json`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (isMissingStatus(response.status)) {
      markMissingHandle(handle);
      return null;
    }

    if (!response.ok) {
      const error = new Error(`Failed to load product full JSON for ${handle}`);
      error.status = response.status;
      throw error;
    }

    const payload = await response.json();
    return payload && typeof payload === 'object' ? payload.product || null : null;
  }

  function load(handle, options) {
    const normalizedHandle = normalizeHandle(handle);
    const config = options && typeof options === 'object' ? options : {};

    if (!normalizedHandle) {
      return Promise.resolve(null);
    }

    if (config.force) {
      readMissingHandleStorage();
      missingHandleCache.delete(normalizedHandle);
      writeMissingHandleStorage();
    } else if (isMissingHandle(normalizedHandle)) {
      return Promise.resolve(null);
    }

    if (!config.force && requestCache.has(normalizedHandle)) {
      return requestCache.get(normalizedHandle);
    }

    const request = enqueue(() => fetchProductJson(normalizedHandle))
      .catch((error) => {
        if (!error || isTransientStatus(error.status)) {
          requestCache.delete(normalizedHandle);
        }
        return null;
      });

    requestCache.set(normalizedHandle, request);
    return request;
  }

  function loadFull(handle, options) {
    const normalizedHandle = normalizeHandle(handle);
    const config = options && typeof options === 'object' ? options : {};

    if (!normalizedHandle) {
      return Promise.resolve(null);
    }

    if (config.force) {
      readMissingHandleStorage();
      missingHandleCache.delete(normalizedHandle);
      writeMissingHandleStorage();
    } else if (isMissingHandle(normalizedHandle)) {
      return Promise.resolve(null);
    }

    if (!config.force && fullRequestCache.has(normalizedHandle)) {
      return fullRequestCache.get(normalizedHandle);
    }

    const request = enqueue(() => fetchProductFullJson(normalizedHandle))
      .catch((error) => {
        if (!error || isTransientStatus(error.status)) {
          fullRequestCache.delete(normalizedHandle);
        }
        return null;
      });

    fullRequestCache.set(normalizedHandle, request);
    return request;
  }

  function normalizeVariantId(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).replace(/[^0-9]/g, '');
    return normalized || null;
  }

  function loadAvailability(handle, options) {
    return load(handle, options).then((product) => {
      if (!product || typeof product !== 'object') return null;

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariant = variants.find((variant) => {
        return variant && (variant.available === true || variant.availableForSale === true);
      });
      const fallbackVariant = variants.find(Boolean);

      return {
        available: product.available === true || Boolean(availableVariant),
        variantId:
          normalizeVariantId(product.variantId) ||
          normalizeVariantId(availableVariant && availableVariant.id) ||
          normalizeVariantId(fallbackVariant && fallbackVariant.id),
      };
    });
  }

  function prefetch(handles, options) {
    const uniqueHandles = Array.from(
      new Set(
        (Array.isArray(handles) ? handles : [])
          .map(normalizeHandle)
          .filter(Boolean),
      ),
    );

    return Promise.all(uniqueHandles.map((handle) => load(handle, options)));
  }

  function clear(handle) {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return;
    readMissingHandleStorage();
    requestCache.delete(normalizedHandle);
    fullRequestCache.delete(normalizedHandle);
    missingHandleCache.delete(normalizedHandle);
    writeMissingHandleStorage();
  }

  function clearAll() {
    requestCache.clear();
    fullRequestCache.clear();
    missingHandleCache.clear();
    writeMissingHandleStorage();
  }

  window.MrMapleProductJsonLoader = {
    load,
    loadFull,
    loadAvailability,
    prefetch,
    clear,
    clearAll,
    isMissing: isMissingHandle,
    markMissing: markMissingHandle,
  };
}());
